import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery } from 'convex/react';

import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { readToken, writeToken, clearToken } from '@/lib/auth-storage';

/**
 * Authentication context.
 *
 * Single source of truth for the current session token and whether the
 * caller is authenticated. The token is read once on mount from
 * localStorage; subsequent changes (sign-in, sign-up, sign-out, takeover)
 * propagate through React state.
 *
 * Reactive `whoami` is the canonical "are we still authenticated"
 * subscription. If it ever resolves to `superseded`, useSessionGuard
 * (separate hook) reacts by clearing the token and showing a banner.
 */

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'superseded';

interface AuthContextValue {
  /** Current session token, or null if signed out. */
  token: string | null;
  /** Resolved status from the reactive `whoami` query. */
  status: AuthStatus;
  /** Authenticated user, populated when status==='authenticated'. */
  user: {
    userId: Id<'users'>;
    email: string;
    displayName: string | null;
  } | null;
  /** Sign in with email + password. Throws on failure (caller maps to friendlyAuthError). */
  signIn: (email: string, password: string) => Promise<void>;
  /** Create an account and sign in atomically. Requires a valid `signupSecret`. */
  signUp: (
    email: string,
    password: string,
    signupSecret: string,
    displayName?: string,
  ) => Promise<void>;
  /** Sign out and clear the local token. */
  signOut: () => Promise<void>;
  /** Forcibly clear local state. Used by useSessionGuard on supersede. */
  clearLocal: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initial token read happens synchronously on first render so that the
  // first `whoami` query already includes the token argument.
  const [token, setToken] = useState<string | null>(() => readToken());

  // `whoami` is reactive — Convex re-runs it whenever the underlying
  // session/user docs change, including when another device takes over
  // (which patches users.activeSessionId and breaks the match).
  const whoamiResult = useQuery(api.auth.whoami, { token });

  const signInMutation = useMutation(api.auth.signIn);
  const signUpMutation = useMutation(api.auth.signUp);
  const signOutMutation = useMutation(api.auth.signOut);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const userAgent =
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
      const { token: newToken } = await signInMutation({
        email,
        password,
        ...(typeof userAgent === 'string' && userAgent.length > 0 ? { userAgent } : {}),
      });
      writeToken(newToken);
      setToken(newToken);
    },
    [signInMutation],
  );

  const signUp = useCallback(
    async (email: string, password: string, signupSecret: string, displayName?: string) => {
      const userAgent =
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
      const { token: newToken } = await signUpMutation({
        email,
        password,
        signupSecret,
        displayName: displayName?.trim() || undefined,
        ...(typeof userAgent === 'string' && userAgent.length > 0 ? { userAgent } : {}),
      });
      writeToken(newToken);
      setToken(newToken);
    },
    [signUpMutation],
  );

  const signOut = useCallback(async () => {
    const current = token;
    // Optimistic local clear so the UI updates instantly.
    clearToken();
    setToken(null);
    if (current) {
      try {
        await signOutMutation({ token: current });
      } catch {
        // Network / already-invalid — local clear is what matters.
      }
    }
  }, [signOutMutation, token]);

  const clearLocal = useCallback(() => {
    clearToken();
    setToken(null);
  }, []);

  // Heartbeat — touchSession every 60s while authenticated.
  // This both updates lastSeenAt and detects supersede early.
  const touchSession = useMutation(api.auth.touchSession);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      if (cancelled) return;
      try {
        const result = await touchSession({ token });
        if (cancelled) return;
        if (result === 'superseded' || result === 'unauthenticated') {
          clearToken();
          setToken(null);
        }
      } catch {
        /* network blip — ignore */
      }
    }, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, touchSession]);

  const value = useMemo<AuthContextValue>(() => {
    let status: AuthStatus = 'loading';
    let user: AuthContextValue['user'] = null;

    if (!token) {
      status = 'unauthenticated';
    } else if (whoamiResult === undefined) {
      status = 'loading';
    } else if (whoamiResult.status === 'authenticated') {
      status = 'authenticated';
      user = {
        userId: whoamiResult.userId,
        email: whoamiResult.email,
        displayName: whoamiResult.displayName,
      };
    } else if (whoamiResult.status === 'superseded') {
      status = 'superseded';
    } else {
      status = 'unauthenticated';
    }

    return { token, status, user, signIn, signUp, signOut, clearLocal };
  }, [token, whoamiResult, signIn, signUp, signOut, clearLocal]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
